// Curved surface (bezier patch) collision: a line-by-line port of
// Wolfenstein: Enemy Territory qcommon/cm_patch.c (+ the cm_polylib.c winding
// helpers it uses). The control grid is subdivided until it is within
// SUBDIVIDE_DISTANCE of the curve, then every grid quad becomes one facet
// (if planar) or two triangle facets, bounded by border planes shared with
// neighbouring facets plus axial and edge bevels.

import { fcross, fdot } from '../f32';
import type { Vec3 } from '../math';

const MAX_GRID_SIZE = 129;
const SUBDIVIDE_DISTANCE = 16;
const PLANE_TRI_EPSILON = 0.1;
const WRAP_POINT_EPSILON = 0.1;
const POINT_EPSILON = 0.1;
const NORMAL_EPSILON = 0.0001;
const DIST_EPSILON = 0.02;
const MAX_MAP_BOUNDS = 65535;
const SIDE_FRONT = 0;
const SIDE_BACK = 1;
const SIDE_ON = 2;

const f32 = Math.fround;

/** [nx, ny, nz, dist] */
export type Plane4 = [number, number, number, number];

export interface PatchPlane {
  plane: Plane4;
  signbits: number;
}

export interface Facet {
  surfacePlane: number;
  numBorders: number;
  borderPlanes: number[];
  borderInward: boolean[];
  borderNoAdjust: boolean[];
}

export interface PatchCollide {
  bounds: [Vec3, Vec3];
  planes: PatchPlane[];
  facets: Facet[];
}

interface Grid {
  width: number;
  height: number;
  wrapWidth: boolean;
  wrapHeight: boolean;
  /** points[column][row] */
  points: Vec3[][];
}

/** VectorNormalize on a float vector; returns the length. */
function normalizeF(v: number[]): number {
  const len = f32(Math.sqrt(fdot(v, v)));
  if (len) {
    const il = f32(1 / len);
    v[0] = f32(v[0] * il);
    v[1] = f32(v[1] * il);
    v[2] = f32(v[2] * il);
  }
  return len;
}

function signbitsForNormal(n: ArrayLike<number>): number {
  let bits = 0;
  for (let j = 0; j < 3; j++) if (n[j] < 0) bits |= 1 << j;
  return bits;
}

/** The normal points out of the clock for clockwise ordered points. */
function planeFromPoints(a: Vec3, b: Vec3, c: Vec3): Plane4 | null {
  const d1 = [f32(b[0] - a[0]), f32(b[1] - a[1]), f32(b[2] - a[2])];
  const d2 = [f32(c[0] - a[0]), f32(c[1] - a[1]), f32(c[2] - a[2])];
  const p: Vec3 = fcross([0, 0, 0], d2, d1);
  if (normalizeF(p) === 0) return null;
  return [p[0], p[1], p[2], f32(fdot(a, p))];
}

// --- grid subdivision ---

function needsSubdivision(a: Vec3, b: Vec3, c: Vec3): boolean {
  const delta = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    // 0.5 is a double literal: the float sums are promoted
    const lmid = f32(0.5 * f32(a[i] + c[i]));
    const cmid = f32(0.5 * (0.5 * f32(a[i] + b[i]) + 0.5 * f32(b[i] + c[i])));
    delta[i] = f32(cmid - lmid);
  }
  return f32(Math.sqrt(fdot(delta, delta))) >= SUBDIVIDE_DISTANCE;
}

function subdivide(a: Vec3, b: Vec3, c: Vec3): [Vec3, Vec3, Vec3] {
  const out1: Vec3 = [0, 0, 0];
  const out2: Vec3 = [0, 0, 0];
  const out3: Vec3 = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    out1[i] = f32(0.5 * f32(a[i] + b[i]));
    out3[i] = f32(0.5 * f32(b[i] + c[i]));
    out2[i] = f32(0.5 * f32(out1[i] + out3[i]));
  }
  return [out1, out2, out3];
}

function transposeGrid(grid: Grid): void {
  const pts: Vec3[][] = [];
  for (let i = 0; i < grid.height; i++) {
    const col: Vec3[] = [];
    for (let j = 0; j < grid.width; j++) col.push(grid.points[j][i]);
    pts.push(col);
  }
  grid.points = pts;
  [grid.width, grid.height] = [grid.height, grid.width];
  [grid.wrapWidth, grid.wrapHeight] = [grid.wrapHeight, grid.wrapWidth];
}

function setGridWrapWidth(grid: Grid): void {
  let i = 0;
  for (; i < grid.height; i++) {
    let j = 0;
    for (; j < 3; j++) {
      const d = grid.points[0][i][j] - grid.points[grid.width - 1][i][j];
      if (d < -WRAP_POINT_EPSILON || d > WRAP_POINT_EPSILON) break;
    }
    if (j !== 3) break;
  }
  grid.wrapWidth = i === grid.height;
}

function subdivideGridColumns(grid: Grid): void {
  for (let i = 0; i < grid.width - 2; ) {
    // points[i] interpolating, points[i+1] approximating, points[i+2] interpolating
    let j = 0;
    for (; j < grid.height; j++) {
      if (needsSubdivision(grid.points[i][j], grid.points[i + 1][j], grid.points[i + 2][j])) break;
    }
    if (j === grid.height) {
      // all of the points were close enough to the linear midpoints
      // that we can collapse the entire column away
      grid.points.splice(i + 1, 1);
      grid.width--;
      i++;
      continue;
    }
    if (grid.width + 2 > MAX_GRID_SIZE) throw new Error('patch grid too large');
    // we need to subdivide the curve
    const c1: Vec3[] = [];
    const c2: Vec3[] = [];
    const c3: Vec3[] = [];
    for (j = 0; j < grid.height; j++) {
      const [o1, o2, o3] = subdivide(grid.points[i][j], grid.points[i + 1][j], grid.points[i + 2][j]);
      c1.push(o1);
      c2.push(o2);
      c3.push(o3);
    }
    grid.points.splice(i + 1, 1, c1, c2, c3);
    grid.width += 2;
    // the new approximating point at i+1 may need to be removed
    // or subdivided farther, so don't advance i
  }
}

function comparePoints(a: Vec3, b: Vec3): boolean {
  for (let k = 0; k < 3; k++) {
    const d = a[k] - b[k];
    if (d < -POINT_EPSILON || d > POINT_EPSILON) return false;
  }
  return true;
}

function removeDegenerateColumns(grid: Grid): void {
  for (let i = 0; i < grid.width - 1; i++) {
    let j = 0;
    for (; j < grid.height; j++) {
      if (!comparePoints(grid.points[i][j], grid.points[i + 1][j])) break;
    }
    if (j !== grid.height) continue; // not degenerate
    grid.points.splice(i + 1, 1);
    grid.width--;
    // check against the next column
    i--;
  }
}

// --- windings (cm_polylib.c) ---

type Winding = Vec3[];

function baseWindingForPlane(normal: ArrayLike<number>, dist: number): Winding {
  // find the major axis
  let max = -MAX_MAP_BOUNDS;
  let x = -1;
  for (let i = 0; i < 3; i++) {
    const v = Math.abs(normal[i]);
    if (v > max) {
      x = i;
      max = v;
    }
  }
  const vup = [0, 0, 0];
  if (x === 0 || x === 1) vup[2] = 1;
  else vup[0] = 1;
  const v = f32(fdot(vup, normal));
  // VectorMA( vup, -v, normal, vup )
  for (let i = 0; i < 3; i++) vup[i] = f32(vup[i] + f32(normal[i] * -v));
  normalizeF(vup);
  const org = [f32(normal[0] * dist), f32(normal[1] * dist), f32(normal[2] * dist)];
  const vright: Vec3 = fcross([0, 0, 0], vup, normal);
  for (let i = 0; i < 3; i++) {
    vup[i] = f32(vup[i] * MAX_MAP_BOUNDS);
    vright[i] = f32(vright[i] * MAX_MAP_BOUNDS);
  }
  const p = (sr: number, su: number): Vec3 => [f32(f32(org[0] + sr * vright[0]) + su * vup[0]), f32(f32(org[1] + sr * vright[1]) + su * vup[1]), f32(f32(org[2] + sr * vright[2]) + su * vup[2])];
  return [p(-1, 1), p(1, 1), p(1, -1), p(-1, -1)];
}

/** Keeps the part of the winding in front of the plane; null if nothing is left. */
function chopWinding(w: Winding, normal: ArrayLike<number>, dist: number, epsilon: number): Winding | null {
  const n = w.length;
  const dists: number[] = new Array(n + 1);
  const sides: number[] = new Array(n + 1);
  const counts = [0, 0, 0];
  for (let i = 0; i < n; i++) {
    const d = f32(f32(fdot(w[i], normal)) - dist);
    dists[i] = d;
    sides[i] = d > epsilon ? SIDE_FRONT : d < -epsilon ? SIDE_BACK : SIDE_ON;
    counts[sides[i]]++;
  }
  sides[n] = sides[0];
  dists[n] = dists[0];
  if (!counts[SIDE_FRONT]) return null;
  if (!counts[SIDE_BACK]) return w;

  const out: Winding = [];
  for (let i = 0; i < n; i++) {
    const p1 = w[i];
    if (sides[i] === SIDE_ON) {
      out.push(p1);
      continue;
    }
    if (sides[i] === SIDE_FRONT) out.push(p1);
    if (sides[i + 1] === SIDE_ON || sides[i + 1] === sides[i]) continue;
    // generate a split point
    const p2 = w[(i + 1) % n];
    const t = f32(dists[i] / f32(dists[i] - dists[i + 1]));
    const mid: Vec3 = [0, 0, 0];
    for (let j = 0; j < 3; j++) {
      // avoid round off error when possible
      if (normal[j] === 1) mid[j] = dist;
      else if (normal[j] === -1) mid[j] = -dist;
      else mid[j] = f32(p1[j] + f32(t * f32(p2[j] - p1[j])));
    }
    out.push(mid);
  }
  return out;
}

function windingBounds(w: Winding): [Vec3, Vec3] {
  const mins: Vec3 = [MAX_MAP_BOUNDS, MAX_MAP_BOUNDS, MAX_MAP_BOUNDS];
  const maxs: Vec3 = [-MAX_MAP_BOUNDS, -MAX_MAP_BOUNDS, -MAX_MAP_BOUNDS];
  for (const p of w) {
    for (let j = 0; j < 3; j++) {
      if (p[j] < mins[j]) mins[j] = p[j];
      if (p[j] > maxs[j]) maxs[j] = p[j];
    }
  }
  return [mins, maxs];
}

// --- patch collide generation ---

class PatchBuilder {
  planes: PatchPlane[] = [];
  facets: Facet[] = [];

  planeEqual(p: PatchPlane, plane: ArrayLike<number>): { equal: boolean; flipped: boolean } {
    const a = p.plane;
    if (
      Math.abs(a[0] - plane[0]) < NORMAL_EPSILON &&
      Math.abs(a[1] - plane[1]) < NORMAL_EPSILON &&
      Math.abs(a[2] - plane[2]) < NORMAL_EPSILON &&
      Math.abs(a[3] - plane[3]) < DIST_EPSILON
    ) {
      return { equal: true, flipped: false };
    }
    if (
      Math.abs(a[0] + plane[0]) < NORMAL_EPSILON &&
      Math.abs(a[1] + plane[1]) < NORMAL_EPSILON &&
      Math.abs(a[2] + plane[2]) < NORMAL_EPSILON &&
      Math.abs(a[3] + plane[3]) < DIST_EPSILON
    ) {
      return { equal: true, flipped: true };
    }
    return { equal: false, flipped: false };
  }

  findPlane2(plane: Plane4): { index: number; flipped: boolean } {
    for (let i = 0; i < this.planes.length; i++) {
      const r = this.planeEqual(this.planes[i], plane);
      if (r.equal) return { index: i, flipped: r.flipped };
    }
    this.planes.push({ plane: [plane[0], plane[1], plane[2], plane[3]], signbits: signbitsForNormal(plane) });
    return { index: this.planes.length - 1, flipped: false };
  }

  findPlane(p1: Vec3, p2: Vec3, p3: Vec3): number {
    const plane = planeFromPoints(p1, p2, p3);
    if (!plane) return -1;
    // see if the points are close enough to an existing plane
    for (let i = 0; i < this.planes.length; i++) {
      const pl = this.planes[i].plane;
      if (fdot(plane, pl) < 0) continue; // allow backwards planes?
      let d = f32(fdot(p1, pl) - pl[3]);
      if (d < -PLANE_TRI_EPSILON || d > PLANE_TRI_EPSILON) continue;
      d = f32(fdot(p2, pl) - pl[3]);
      if (d < -PLANE_TRI_EPSILON || d > PLANE_TRI_EPSILON) continue;
      d = f32(fdot(p3, pl) - pl[3]);
      if (d < -PLANE_TRI_EPSILON || d > PLANE_TRI_EPSILON) continue;
      return i;
    }
    this.planes.push({ plane, signbits: signbitsForNormal(plane) });
    return this.planes.length - 1;
  }

  pointOnPlaneSide(p: Vec3, planeNum: number): number {
    if (planeNum === -1) return SIDE_ON;
    const plane = this.planes[planeNum].plane;
    const d = f32(fdot(p, plane) - plane[3]);
    if (d > PLANE_TRI_EPSILON) return SIDE_FRONT;
    if (d < -PLANE_TRI_EPSILON) return SIDE_BACK;
    return SIDE_ON;
  }

  gridPlane(gridPlanes: number[][][], i: number, j: number, tri: number): number {
    let p = gridPlanes[i][j][tri];
    if (p !== -1) return p;
    p = gridPlanes[i][j][tri ? 0 : 1];
    if (p !== -1) return p;
    return -1; // should never happen
  }

  edgePlaneNum(grid: Grid, gridPlanes: number[][][], i: number, j: number, k: number): number {
    const up = (p1: Vec3, p: number): Vec3 => {
      if (p === -1) return p1; // "should never happen" in the original; yields a degenerate plane
      const n = this.planes[p].plane;
      return [f32(p1[0] + 4 * n[0]), f32(p1[1] + 4 * n[1]), f32(p1[2] + 4 * n[2])];
    };
    const g = grid.points;
    switch (k) {
      case 0: {
        // top border
        const p1 = g[i][j];
        const p2 = g[i + 1][j];
        return this.findPlane(p1, p2, up(p1, this.gridPlane(gridPlanes, i, j, 0)));
      }
      case 2: {
        // bottom border
        const p1 = g[i][j + 1];
        const p2 = g[i + 1][j + 1];
        return this.findPlane(p2, p1, up(p1, this.gridPlane(gridPlanes, i, j, 1)));
      }
      case 3: {
        // left border
        const p1 = g[i][j];
        const p2 = g[i][j + 1];
        return this.findPlane(p2, p1, up(p1, this.gridPlane(gridPlanes, i, j, 1)));
      }
      case 1: {
        // right border
        const p1 = g[i + 1][j];
        const p2 = g[i + 1][j + 1];
        return this.findPlane(p1, p2, up(p1, this.gridPlane(gridPlanes, i, j, 0)));
      }
      case 4: {
        // diagonal out of triangle 0
        const p1 = g[i + 1][j + 1];
        const p2 = g[i][j];
        return this.findPlane(p1, p2, up(p1, this.gridPlane(gridPlanes, i, j, 0)));
      }
      case 5: {
        // diagonal out of triangle 1
        const p1 = g[i][j];
        const p2 = g[i + 1][j + 1];
        return this.findPlane(p1, p2, up(p1, this.gridPlane(gridPlanes, i, j, 1)));
      }
    }
    throw new Error('CM_EdgePlaneNum: bad k');
  }

  setBorderInward(facet: Facet, grid: Grid, i: number, j: number, which: number): void {
    const g = grid.points;
    let points: Vec3[];
    if (which === -1) points = [g[i][j], g[i + 1][j], g[i + 1][j + 1], g[i][j + 1]];
    else if (which === 0) points = [g[i][j], g[i + 1][j], g[i + 1][j + 1]];
    else points = [g[i + 1][j + 1], g[i][j + 1], g[i][j]];

    for (let k = 0; k < facet.numBorders; k++) {
      let front = 0;
      let back = 0;
      for (const p of points) {
        const side = this.pointOnPlaneSide(p, facet.borderPlanes[k]);
        if (side === SIDE_FRONT) front++;
        if (side === SIDE_BACK) back++;
      }
      if (front && !back) facet.borderInward[k] = true;
      else if (back && !front) facet.borderInward[k] = false;
      else if (!front && !back) facet.borderPlanes[k] = -1; // flat side border
      else facet.borderInward[k] = false; // bisecting side border
    }
  }

  /** Border plane as clipping plane (pointing into the facet's kept side). */
  private borderClipPlane(facet: Facet, k: number): Plane4 {
    const p = this.planes[facet.borderPlanes[k]].plane;
    return facet.borderInward[k] ? [p[0], p[1], p[2], p[3]] : [-p[0], -p[1], -p[2], -p[3]];
  }

  /** If the facet isn't bounded by its borders, we screwed up. */
  validateFacet(facet: Facet): boolean {
    if (facet.surfacePlane === -1) return false;
    const sp = this.planes[facet.surfacePlane].plane;
    let w: Winding | null = baseWindingForPlane(sp, sp[3]);
    for (let j = 0; j < facet.numBorders && w; j++) {
      if (facet.borderPlanes[j] === -1) return false;
      const plane = this.borderClipPlane(facet, j);
      w = chopWinding(w, plane, plane[3], 0.1);
    }
    if (!w) return false; // winding was completely chopped away
    // see if the facet is unreasonably large
    const [mins, maxs] = windingBounds(w);
    for (let j = 0; j < 3; j++) {
      if (maxs[j] - mins[j] > MAX_MAP_BOUNDS) return false; // we must be missing a plane
      if (mins[j] >= MAX_MAP_BOUNDS) return false;
      if (maxs[j] <= -MAX_MAP_BOUNDS) return false;
    }
    return true;
  }

  private addBorder(facet: Facet, planeNum: number, inward: boolean): void {
    facet.borderPlanes[facet.numBorders] = planeNum;
    facet.borderNoAdjust[facet.numBorders] = false;
    facet.borderInward[facet.numBorders] = inward;
    facet.numBorders++;
  }

  addFacetBevels(facet: Facet): void {
    const sp = this.planes[facet.surfacePlane].plane;
    let w: Winding | null = baseWindingForPlane(sp, sp[3]);
    for (let j = 0; j < facet.numBorders && w; j++) {
      if (facet.borderPlanes[j] === facet.surfacePlane) continue;
      const plane = this.borderClipPlane(facet, j);
      w = chopWinding(w, plane, plane[3], 0.1);
    }
    if (!w) return;

    const [mins, maxs] = windingBounds(w);

    // add the axial planes
    for (let axis = 0; axis < 3; axis++) {
      for (let dir = -1; dir <= 1; dir += 2) {
        const plane: Plane4 = [0, 0, 0, 0];
        plane[axis] = dir;
        plane[3] = dir === 1 ? maxs[axis] : -mins[axis];
        // if it's the surface plane
        if (this.planeEqual(this.planes[facet.surfacePlane], plane).equal) continue;
        // see if the plane is already present
        let i = 0;
        for (; i < facet.numBorders; i++) {
          const bp = this.planes[facet.borderPlanes[i]].plane;
          if (dir > 0 ? bp[axis] >= 0.9999 : bp[axis] <= -0.9999) break;
        }
        if (i === facet.numBorders) {
          const r = this.findPlane2(plane);
          this.addBorder(facet, r.index, r.flipped);
        }
      }
    }

    // add the edge bevels: test the non-axial plane edges
    for (let j = 0; j < w.length; j++) {
      const k1 = (j + 1) % w.length;
      const vec = [f32(w[j][0] - w[k1][0]), f32(w[j][1] - w[k1][1]), f32(w[j][2] - w[k1][2])];
      // if it's a degenerate edge
      if (normalizeF(vec) < 0.5) continue;
      // CM_SnapVector
      for (let i = 0; i < 3; i++) {
        if (Math.abs(vec[i] - 1) < NORMAL_EPSILON) {
          vec[0] = vec[1] = vec[2] = 0;
          vec[i] = 1;
          break;
        }
        if (Math.abs(vec[i] + 1) < NORMAL_EPSILON) {
          vec[0] = vec[1] = vec[2] = 0;
          vec[i] = -1;
          break;
        }
      }
      let k = 0;
      for (; k < 3; k++) {
        if (vec[k] === -1 || vec[k] === 1 || (vec[k] === 0 && vec[(k + 1) % 3] === 0)) break; // axial
      }
      if (k < 3) continue; // only test non-axial edges

      // try the six possible slanted axials from this edge
      for (let axis = 0; axis < 3; axis++) {
        for (let dir = -1; dir <= 1; dir += 2) {
          // construct a plane
          const vec2 = [0, 0, 0];
          vec2[axis] = dir;
          const pn: Vec3 = fcross([0, 0, 0], vec, vec2);
          if (normalizeF(pn) < 0.5) continue;
          const plane: Plane4 = [pn[0], pn[1], pn[2], f32(fdot(w[j], pn))];

          // if all the points of the facet winding are behind this plane, it is a proper edge bevel
          let minBack = 0;
          let l = 0;
          for (; l < w.length; l++) {
            const d = f32(fdot(w[l], plane) - plane[3]);
            if (d > 0.1) break; // point in front
            if (d < minBack) minBack = d;
          }
          // if some point was at the front
          if (l < w.length) continue;
          // if no points at the back then the winding is on the bevel plane
          if (minBack > -0.1) break;
          // if it's the surface plane
          if (this.planeEqual(this.planes[facet.surfacePlane], plane).equal) continue;
          // see if the plane is already present
          let i = 0;
          for (; i < facet.numBorders; i++) {
            if (this.planeEqual(this.planes[facet.borderPlanes[i]], plane).equal) break;
          }
          if (i === facet.numBorders) {
            const r = this.findPlane2(plane);
            const np = this.planes[r.index].plane;
            const chop: Plane4 = r.flipped ? [np[0], np[1], np[2], np[3]] : [-np[0], -np[1], -np[2], -np[3]];
            if (!chopWinding(w.slice(), chop, chop[3], 0.1)) continue; // invalid bevel
            this.addBorder(facet, r.index, r.flipped);
          }
        }
      }
    }

    // add opposite plane
    this.addBorder(facet, facet.surfacePlane, true);
  }

  fromGrid(grid: Grid): void {
    const W = grid.width;
    const H = grid.height;
    const g = grid.points;
    const gridPlanes: number[][][] = [];
    // find the planes for each triangle of the grid
    for (let i = 0; i < W - 1; i++) {
      gridPlanes.push([]);
      for (let j = 0; j < H - 1; j++) {
        gridPlanes[i].push([this.findPlane(g[i][j], g[i + 1][j], g[i + 1][j + 1]), this.findPlane(g[i + 1][j + 1], g[i][j + 1], g[i][j])]);
      }
    }

    const EN_TOP = 0;
    const EN_RIGHT = 1;
    const EN_BOTTOM = 2;
    const EN_LEFT = 3;
    // create the borders for each facet
    for (let i = 0; i < W - 1; i++) {
      for (let j = 0; j < H - 1; j++) {
        const borders = [-1, -1, -1, -1];
        const noAdjust = [false, false, false, false];

        if (j > 0) borders[EN_TOP] = gridPlanes[i][j - 1][1];
        else if (grid.wrapHeight) borders[EN_TOP] = gridPlanes[i][H - 2][1];
        noAdjust[EN_TOP] = borders[EN_TOP] === gridPlanes[i][j][0];
        if (borders[EN_TOP] === -1 || noAdjust[EN_TOP]) borders[EN_TOP] = this.edgePlaneNum(grid, gridPlanes, i, j, 0);

        if (j < H - 2) borders[EN_BOTTOM] = gridPlanes[i][j + 1][0];
        else if (grid.wrapHeight) borders[EN_BOTTOM] = gridPlanes[i][0][0];
        noAdjust[EN_BOTTOM] = borders[EN_BOTTOM] === gridPlanes[i][j][1];
        if (borders[EN_BOTTOM] === -1 || noAdjust[EN_BOTTOM]) borders[EN_BOTTOM] = this.edgePlaneNum(grid, gridPlanes, i, j, 2);

        if (i > 0) borders[EN_LEFT] = gridPlanes[i - 1][j][0];
        else if (grid.wrapWidth) borders[EN_LEFT] = gridPlanes[W - 2][j][0];
        noAdjust[EN_LEFT] = borders[EN_LEFT] === gridPlanes[i][j][1];
        if (borders[EN_LEFT] === -1 || noAdjust[EN_LEFT]) borders[EN_LEFT] = this.edgePlaneNum(grid, gridPlanes, i, j, 3);

        if (i < W - 2) borders[EN_RIGHT] = gridPlanes[i + 1][j][1];
        else if (grid.wrapWidth) borders[EN_RIGHT] = gridPlanes[0][j][1];
        noAdjust[EN_RIGHT] = borders[EN_RIGHT] === gridPlanes[i][j][0];
        if (borders[EN_RIGHT] === -1 || noAdjust[EN_RIGHT]) borders[EN_RIGHT] = this.edgePlaneNum(grid, gridPlanes, i, j, 1);

        const newFacet = (surfacePlane: number, b: number[], na: boolean[]): Facet => ({
          surfacePlane,
          numBorders: b.length,
          borderPlanes: b.slice(),
          borderInward: b.map(() => false),
          borderNoAdjust: na.slice(),
        });

        if (gridPlanes[i][j][0] === gridPlanes[i][j][1]) {
          if (gridPlanes[i][j][0] === -1) continue; // degenerate
          const facet = newFacet(
            gridPlanes[i][j][0],
            [borders[EN_TOP], borders[EN_RIGHT], borders[EN_BOTTOM], borders[EN_LEFT]],
            [noAdjust[EN_TOP], noAdjust[EN_RIGHT], noAdjust[EN_BOTTOM], noAdjust[EN_LEFT]],
          );
          this.setBorderInward(facet, grid, i, j, -1);
          if (this.validateFacet(facet)) {
            this.addFacetBevels(facet);
            this.facets.push(facet);
          }
        } else {
          // two separate triangles
          let b2 = gridPlanes[i][j][1];
          if (b2 === -1) {
            b2 = borders[EN_BOTTOM];
            if (b2 === -1) b2 = this.edgePlaneNum(grid, gridPlanes, i, j, 4);
          }
          const f0 = newFacet(gridPlanes[i][j][0], [borders[EN_TOP], borders[EN_RIGHT], b2], [noAdjust[EN_TOP], noAdjust[EN_RIGHT], false]);
          this.setBorderInward(f0, grid, i, j, 0);
          if (this.validateFacet(f0)) {
            this.addFacetBevels(f0);
            this.facets.push(f0);
          }

          let b3 = gridPlanes[i][j][0];
          if (b3 === -1) {
            b3 = borders[EN_TOP];
            if (b3 === -1) b3 = this.edgePlaneNum(grid, gridPlanes, i, j, 5);
          }
          const f1 = newFacet(gridPlanes[i][j][1], [borders[EN_BOTTOM], borders[EN_LEFT], b3], [noAdjust[EN_BOTTOM], noAdjust[EN_LEFT], false]);
          this.setBorderInward(f1, grid, i, j, 1);
          if (this.validateFacet(f1)) {
            this.addFacetBevels(f1);
            this.facets.push(f1);
          }
        }
      }
    }
  }
}

/**
 * CM_GeneratePatchCollide.
 * @param points control points packed as concatenated rows (points[row * width + col])
 */
export function generatePatchCollide(width: number, height: number, points: Vec3[]): PatchCollide | null {
  if (width <= 2 || height <= 2 || !(width & 1) || !(height & 1) || width > MAX_GRID_SIZE || height > MAX_GRID_SIZE) return null;

  const grid: Grid = { width, height, wrapWidth: false, wrapHeight: false, points: [] };
  for (let i = 0; i < width; i++) {
    const col: Vec3[] = [];
    for (let j = 0; j < height; j++) {
      const p = points[j * width + i];
      col.push([f32(p[0]), f32(p[1]), f32(p[2])]);
    }
    grid.points.push(col);
  }

  // subdivide the grid
  setGridWrapWidth(grid);
  subdivideGridColumns(grid);
  removeDegenerateColumns(grid);

  transposeGrid(grid);

  setGridWrapWidth(grid);
  subdivideGridColumns(grid);
  removeDegenerateColumns(grid);

  // we now have a grid of points exactly on the curve
  const mins: Vec3 = [Infinity, Infinity, Infinity];
  const maxs: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < grid.width; i++) {
    for (let j = 0; j < grid.height; j++) {
      const p = grid.points[i][j];
      for (let k = 0; k < 3; k++) {
        if (p[k] < mins[k]) mins[k] = p[k];
        if (p[k] > maxs[k]) maxs[k] = p[k];
      }
    }
  }

  const builder = new PatchBuilder();
  builder.fromGrid(grid);

  // expand by one unit for epsilon purposes
  for (let k = 0; k < 3; k++) {
    mins[k] -= 1;
    maxs[k] += 1;
  }
  return { bounds: [mins, maxs], planes: builder.planes, facets: builder.facets };
}

// --- trace testing ---

export const SURFACE_CLIP_EPSILON = 0.125;

/** Minimal view of the trace work needed by the patch code. */
export interface PatchTraceWork {
  start: Vec3;
  end: Vec3;
  offsets: Vec3[];
  isPoint: boolean;
  trace: { fraction: number; plane: { normal: Vec3; dist: number } };
}

interface FacetState {
  enterFrac: number;
  leaveFrac: number;
  hit: boolean;
}

/** CM_CheckFacetPlane; returns false when the trace is completely in front of the plane. */
function checkFacetPlane(plane: ArrayLike<number>, start: Vec3, end: Vec3, s: FacetState): boolean {
  s.hit = false;
  const d1 = f32(fdot(start, plane) - plane[3]);
  const d2 = f32(fdot(end, plane) - plane[3]);
  // if completely in front of face, no intersection with the entire facet
  if (d1 > 0 && (d2 >= SURFACE_CLIP_EPSILON || d2 >= d1)) return false;
  // if it doesn't cross the plane, the plane isn't relevant
  if (d1 <= 0 && d2 <= 0) return true;
  // crosses face
  if (d1 > d2) {
    // enter
    let fr = f32((d1 - SURFACE_CLIP_EPSILON) / f32(d1 - d2));
    if (fr < 0) fr = 0;
    // always favor previous plane hits and thus also the surface plane hit
    if (fr > s.enterFrac) {
      s.enterFrac = fr;
      s.hit = true;
    }
  } else {
    // leave
    let fr = f32((d1 + SURFACE_CLIP_EPSILON) / f32(d1 - d2));
    if (fr > 1) fr = 1;
    if (fr < s.leaveFrac) s.leaveFrac = fr;
  }
  return true;
}

export function traceThroughPatchCollide(tw: PatchTraceWork, pc: PatchCollide): void {
  // point traces (CM_TracePointThroughPatchCollide) are not needed for player movement
  if (tw.isPoint) return;
  const s: FacetState = { enterFrac: 0, leaveFrac: 0, hit: false };
  const plane: Plane4 = [0, 0, 0, 0];
  const best: Plane4 = [0, 0, 0, 0];

  for (const facet of pc.facets) {
    s.enterFrac = -1;
    s.leaveFrac = 1;
    let hitnum = -1;

    let pp = pc.planes[facet.surfacePlane];
    plane[0] = pp.plane[0];
    plane[1] = pp.plane[1];
    plane[2] = pp.plane[2];
    plane[3] = f32(pp.plane[3] - fdot(tw.offsets[pp.signbits], plane));

    if (!checkFacetPlane(plane, tw.start, tw.end, s)) continue;
    if (s.hit) best.splice(0, 4, ...plane);

    let j = 0;
    for (; j < facet.numBorders; j++) {
      pp = pc.planes[facet.borderPlanes[j]];
      const sign = facet.borderInward[j] ? -1 : 1;
      plane[0] = sign * pp.plane[0];
      plane[1] = sign * pp.plane[1];
      plane[2] = sign * pp.plane[2];
      plane[3] = sign * pp.plane[3];
      // NOTE: this works even though the plane might be flipped because the bbox is centered
      plane[3] = f32(plane[3] + Math.abs(fdot(tw.offsets[pp.signbits], plane)));
      if (!checkFacetPlane(plane, tw.start, tw.end, s)) break;
      if (s.hit) {
        hitnum = j;
        best.splice(0, 4, ...plane);
      }
    }
    if (j < facet.numBorders) continue;
    // never clip against the back side
    if (hitnum === facet.numBorders - 1) continue;

    if (s.enterFrac < s.leaveFrac && s.enterFrac >= 0 && s.enterFrac < tw.trace.fraction) {
      tw.trace.fraction = s.enterFrac;
      tw.trace.plane = { normal: [best[0], best[1], best[2]], dist: best[3] };
    }
  }
}

export function positionTestInPatchCollide(tw: PatchTraceWork, pc: PatchCollide): boolean {
  if (tw.isPoint) return false;
  const plane: Plane4 = [0, 0, 0, 0];
  for (const facet of pc.facets) {
    let pp = pc.planes[facet.surfacePlane];
    plane[0] = pp.plane[0];
    plane[1] = pp.plane[1];
    plane[2] = pp.plane[2];
    plane[3] = f32(pp.plane[3] - fdot(tw.offsets[pp.signbits], plane));
    if (f32(fdot(plane, tw.start) - plane[3]) > 0) continue;

    let j = 0;
    for (; j < facet.numBorders; j++) {
      pp = pc.planes[facet.borderPlanes[j]];
      const sign = facet.borderInward[j] ? -1 : 1;
      plane[0] = sign * pp.plane[0];
      plane[1] = sign * pp.plane[1];
      plane[2] = sign * pp.plane[2];
      plane[3] = sign * pp.plane[3];
      plane[3] = f32(plane[3] + Math.abs(fdot(tw.offsets[pp.signbits], plane)));
      if (f32(fdot(plane, tw.start) - plane[3]) > 0) break;
    }
    if (j < facet.numBorders) continue;
    // inside this patch facet
    return true;
  }
  return false;
}
